import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Radio, Stack, Typography,
} from '@mui/material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { mergeCustomerContacts } from '../../api/communicationApi';
import T, { useAutoTranslate } from '../common/T';

/**
 * Two or more contacts own the same phone or e-mail. Pick the one to keep;
 * the others are archived as "merged" (nothing is deleted) and their chats,
 * numbers and e-mails move to the kept contact.
 */
const DuplicateReviewDialog = ({ open, contacts, onClose, onMerged }) => {
  const at = useAutoTranslate();
  const [keepId, setKeepId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      const firstActive = (contacts || []).find((item) => item && !item.archived);
      setKeepId(firstActive?.id || contacts?.[0]?.id || '');
      setError('');
    }
  }, [open, contacts]);

  const list = (contacts || []).filter(Boolean);
  const keep = list.find((item) => item.id === keepId);
  const others = list.filter((item) => item.id !== keepId);

  const merge = async () => {
    setBusy(true);
    setError('');
    try {
      for (const duplicate of others) {
        // eslint-disable-next-line no-await-in-loop
        await mergeCustomerContacts(duplicate.id, keepId);
      }
      if (typeof onMerged === 'function') onMerged(keep);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'The contacts could not be merged.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MergeTypeIcon sx={{ color: '#b45309' }} />
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: 18 }}><T>These contacts share the same details</T></Typography>
          <Typography variant="caption" color="text.secondary"><T>Choose the contact to keep. The others are merged into it.</T></Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}><T>{error}</T></Alert>}
        <Stack spacing={1}>
          {list.map((item) => (
            <Box
              key={item.id}
              component="label"
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25, borderRadius: '10px', cursor: 'pointer', bgcolor: '#fff',
                border: '2px solid', borderColor: item.id === keepId ? '#32cd32' : '#e5e7eb',
              }}
            >
              <Radio checked={item.id === keepId} onChange={() => setKeepId(item.id)} size="small" sx={{ p: 0.5, '&.Mui-checked': { color: '#32cd32' } }} inputProps={{ 'aria-label': `Keep ${item.name || 'contact'}` }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 900, fontSize: 14 }}>{item.name || 'Unnamed contact'}</Typography>
                  {item.id === keepId && <Chip size="small" label={at("Keep")} sx={{ height: 20, bgcolor: '#dcfce7', color: '#166534', fontWeight: 800 }} />}
                  {item.id !== keepId && <Chip size="small" label={at("Merge into kept contact")} sx={{ height: 20, bgcolor: '#fef3c7', color: '#92400e', fontWeight: 800 }} />}
                  {item.archived && <Chip size="small" label={at("Archived")} sx={{ height: 20 }} />}
                </Stack>
                <Typography sx={{ fontSize: 12.5, color: '#475569', overflowWrap: 'anywhere' }}>
                  {[item.email, item.phone].filter(Boolean).join(' · ') || 'No e-mail or phone'}
                </Typography>
                {(item.matched_on || []).length > 0 && (
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                    {`Matched on ${(item.matched_on || []).map((how) => how.replace('extra_', 'other ')).join(', ')}`}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
        <Typography sx={{ mt: 1.5, fontSize: 12.5, color: '#475569' }}><T>
          Nothing is deleted. Merged contacts stay archived with a link to the kept contact, so repairs and invoices keep working, and a merge can be undone by restoring the contact.
        </T></Typography>
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700 }}><T>Cancel</T></Button>
        <Button
          variant="contained"
          onClick={merge}
          disabled={busy || !keepId || others.length === 0}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <MergeTypeIcon />}
          sx={{ textTransform: 'none', fontWeight: 900, bgcolor: '#32cd32', '&:hover': { bgcolor: '#28a428' } }}
        >
          {others.length === 1 ? `Merge 1 contact into ${keep?.name || 'the kept contact'}` : `Merge ${others.length} contacts into ${keep?.name || 'the kept contact'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DuplicateReviewDialog.propTypes = {
  open: PropTypes.bool,
  contacts: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func,
  onMerged: PropTypes.func,
};

export default DuplicateReviewDialog;
